import { createInstance } from '@/helpers/api/base'
import { useQueryClient } from '@tanstack/react-query'
import { useAppMutation } from '@/helpers/api/mutation'
import { switchUserSchema } from '../ui/SwitchUser/schema'
import { z } from 'zod'
import { useAuthStore } from '@/store/auth-store'
import { useGetUserInfo } from './useGetUserInfo'
import { useModal } from '@/components/Modal/hooks/useModal'
import { useNotification } from '@/ui/Notification'
import styles from '../ui/SwitchUser/SwitchUserModal.module.scss'
import { IUserShift } from '@/types/user'

type FormValues = z.infer<typeof switchUserSchema>

interface ISwitchUserRequest {
    badgeNumber?: string | null
    busHubCode: string | null
    dutyCode?: string | null
    author: string
}

export const useSwitchUser = () => {
    const { setXUserShiftId } = useAuthStore()
    const { data: userInfo } = useGetUserInfo()
    const queryClient = useQueryClient()
    const { closeModal } = useModal()
    const { showNotification } = useNotification()

    const switchUserMutation = useAppMutation<IUserShift, Error, ISwitchUserRequest>({
        mutationFn: (data) =>
            createInstance({
                url: '/user-shifts/switch-user',
                method: 'POST',
                data,
            }),
        onSuccess: async (data) => {
            await setXUserShiftId(data.id)
            await queryClient.invalidateQueries({ queryKey: ['user-info'] })
            closeModal()
            showNotification({
                message: (
                    <div className={styles.notificationMessage}>
                        <span className={styles.notificationMessageTitle}>Creating content as</span>
                        <span className={styles.notificationMessageAuthor}>{data.authorName}</span>
                        <span className={styles.notificationMessageUser}>{userInfo?.userDetails.name} logged in</span>
                    </div>
                ),
                type: 'submit',
            })
        },
    })

    const handleSwitchUser = (data: FormValues) => {
        if (!userInfo) throw new Error('User info not found')

        switchUserMutation.mutate({
            badgeNumber: data.noBadge ? null : data.badgeNumber,
            busHubCode: userInfo.lastSessionDetails.busHubCode,
            dutyCode: userInfo.lastSessionDetails.dutyCode,
            author: data.author,
        })
    }

    return { handleSwitchUser, isPending: switchUserMutation.isPending }
}
